import React, { useState } from 'react';

import { DocumentDownloadOptions, Fonts, TextAlignment } from '../../../../../backend/src/types/types';
import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/ShadCnSelect';
import { Switch } from '@/components/atoms/switch';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import { FONT_FAMILY_OPTIONS, TEXT_ALIGNMENT_OPTIONS } from '@/constants/downloadOptions';
import { cn } from '@/helpers/shadCnUtils';

interface ThreadDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (options: DocumentDownloadOptions, includeSourceDocuments?: boolean) => void;
  isLoading?: boolean;
}

const ThreadDownloadModal: React.FC<ThreadDownloadModalProps> = ({
  isOpen,
  onClose,
  onDownload,
  isLoading = false,
}) => {
  const [options, setOptions] = useState<DocumentDownloadOptions>({
    fontFamily: Fonts.TimesNewRoman,
    textAlignment: TextAlignment.Left,
    includeReferences: true,
  });
  const [includeSourceDocuments, setIncludeSourceDocuments] = useState(false);

  const handleDownload = () => {
    onDownload(options, includeSourceDocuments);
  };

  return (
    <CustomModal
      isOpen={isOpen}
      handleClose={onClose}
      maxWidth="36rem"
      content={
        <div className="flex flex-col">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Download Options</h3>
          </div>

          <div className="px-6 py-4">
            <div className="grid gap-6 py-2">
              {/* Font Family Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Font</Label>
                <Select
                  value={options.fontFamily}
                  onValueChange={(value: Fonts) => setOptions({ ...options, fontFamily: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select font" />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_FAMILY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Text Alignment Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Text Alignment</Label>
                <Select
                  value={options.textAlignment}
                  onValueChange={(value: TextAlignment) => setOptions({ ...options, textAlignment: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select alignment" />
                  </SelectTrigger>
                  <SelectContent>
                    {TEXT_ALIGNMENT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Include References Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <Label className="text-sm font-medium">Include References</Label>
                  <span className="mt-1 text-xs text-gray-500">
                    {options.includeReferences
                      ? 'References section will be added at the end'
                      : 'No references will be included'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Label
                    className={cn(
                      'm-0 px-2 text-sm text-gray-700 transition-colors duration-150',
                      !options.includeReferences && 'text-brandSecondary',
                    )}
                  >
                    No
                  </Label>
                  <Switch
                    className="bg-gray-100"
                    checked={options.includeReferences}
                    onCheckedChange={(checked) => setOptions({ ...options, includeReferences: checked })}
                  />
                  <Label
                    className={cn(
                      'm-0 text-sm text-gray-700 transition-colors duration-150',
                      options.includeReferences && 'text-brandSecondary',
                    )}
                  >
                    Yes
                  </Label>
                </div>
              </div>

              {/* Include Source Documents Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <Label className="text-sm font-medium">Include Source Documents</Label>
                  <span className="mt-1 text-xs text-gray-500">
                    {includeSourceDocuments
                      ? 'Answer and all source documents will be packaged as a zip file'
                      : 'Download answer document only'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Label
                    className={cn(
                      'text-sm text-gray-700 transition-colors duration-150',
                      !includeSourceDocuments && 'text-brandSecondary',
                    )}
                  >
                    No
                  </Label>
                  <Switch
                    className="bg-gray-100"
                    checked={includeSourceDocuments}
                    onCheckedChange={setIncludeSourceDocuments}
                  />
                  <Label
                    className={cn(
                      'text-sm text-gray-700 transition-colors duration-150',
                      includeSourceDocuments && 'text-brandSecondary',
                    )}
                  >
                    Yes
                  </Label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-between gap-3">
              <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDownload} disabled={isLoading}>
                {isLoading ? 'Downloading...' : 'Download'}
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default ThreadDownloadModal;
